/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { shape, func, string, bool, array, object } from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import BlackTieIcon from 'components/BlackTieIcon/BlackTieIcon';
import moment from 'moment';

import {
  getMessage as getMessageAction,
  sendMessage as sendMessageAction,
} from 'actions/inbox';

import { createUrl } from 'utils/urlHelper';

import SpinnerIcon from 'components/SpinnerIcon/SpinnerIcon';
import MessageActions from 'components/MessageActions/MessageActions';
import Chat from 'components/Chat/Chat';
import Avatar from 'components/Avatar/Avatar';

import styles from './message.scss';

/**
 * This is the Message component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 * @author Pelle Lundgren <pelle.lundgren@vntrs.com>
 */
export class Message extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    match: shape({
      params: shape({
        discussionId: string,
      }).isRequired,
    }).isRequired,
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    user: object.isRequired,
    messages: shape({
      loading: bool.isRequired,
      items: array.isRequired,
    }).isRequired,
    message: object.isRequired,
    getMessage: func.isRequired,
    sendMessage: func.isRequired,
  };

  componentDidMount() {
    const {
      auth: { token },
      match: { params },
      getMessage,
    } = this.props;

    // If we have a query.
    if (params.discussionId) {
      getMessage(token, params.discussionId);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      match: { params: prevParams },
    } = prevProps;

    const {
      auth: { token },
      match: { params },
      messages,
      message: currentMessage,
      getMessage,
    } = this.props;

    // New message to display.
    if (
      prevParams.discussionId !== params.discussionId &&
      params.discussionId
    ) {
      getMessage(token, params.discussionId);
    }

    // If we dont have any discussionId we load the first message by default.
    if (!params.discussionId && !messages.loading) {
      const firstMessage = messages.items[0] || null;

      if (
        firstMessage &&
        firstMessage.discussion_id &&
        !currentMessage.loading &&
        firstMessage.discussion_id !== currentMessage.discussion_id
      ) {
        getMessage(token, firstMessage.discussion_id);
      }
    }
  }

  // refetch message ever x millisecond
  pollMessage = discussionId => {
    // 30 seconds
    const POLLING_TIMER = 1000 * 30;
    const {
      auth: { token },
      getMessage,
    } = this.props;

    return setInterval(() => {
      getMessage(token, discussionId, true);
    }, POLLING_TIMER);
  };

  onSubmit = data => {
    const {
      auth: { token },
      message: { discussion_id },
      sendMessage,
    } = this.props;

    sendMessage(token, discussion_id, data);
  };

  /**
   * Render Message.
   *
   * @return {Object}
   */
  render() {
    const { user, message } = this.props;

    const {
      loading,
      discussion_id,
      talent_id,
      job_id,
      job_title,
      job_picture,
      discussion_status,
      button1_type,
      button2_type,
      company_name,
      talent_fullname,
      talent_picture,
      talent_title,
      contract,
    } = message;

    const avatar_url = user?.is_talent ? job_picture : talent_picture;
    const name = user?.is_talent ? company_name : talent_fullname;
    const title = user?.is_talent ? company_name : talent_title;

    const dates = contract?.dates
      .split(',')
      .map(date => moment(date).format('MMM DD, YYYY'))
      .join(', ');

    const price = contract?.total_fee;
    const currency = contract?.currency;

    const private_chat = discussion_status !== 'Team Chat';

    return (
      <section className={styles.wrapper}>
        <header className={styles.header}>
          <div className={styles.textWrapper}>
            <div className={styles.infoWrapper}>
              <Avatar src={avatar_url?.square_url} className={styles.avatar}>
                {name}
              </Avatar>
              <div className={styles.infoContainer}>
                <h2 className={styles.title}>{name}</h2>
                <span className={styles.info}>
                  {title} ·{' '}
                  {private_chat
                    ? `This is a private conversation regarding ${job_title}`
                    : `This is a group conversion for everyone in ${job_title}`}
                </span>
              </div>
            </div>
          </div>
          {private_chat && (
            <div className={styles.contract}>
              <div className={styles.sections}>
                <div className={styles.section}>
                  <p className={styles.top}>{discussion_status}</p>
                  <p className={styles.top}>
                    {price} {currency}
                  </p>
                  <p className={styles.bottom}>
                    <BlackTieIcon
                      icon="calendar"
                      weight="btr"
                      className={styles.icon}
                    />
                    {dates}
                  </p>
                </div>
                <div className={styles.section}>
                  <p className={styles.bottom}>
                    <span className={styles.info}>
                      The job can be cancelled up to 48 hours in advance.
                    </span>
                  </p>
                </div>
                <div className={styles.section}>
                  <p className={styles.bottom}>
                    <span className={styles.info}>
                      Make sure the fee is right before you confim.
                    </span>
                  </p>
                </div>
                <div className={styles.section}>
                  {!loading && discussion_id && (
                    <MessageActions
                      discussionId={discussion_id}
                      jobId={job_id}
                      firstButtonType={button1_type}
                      secondButtonType={button2_type}
                      talentId={talent_id}
                      callbackPath={createUrl('message', discussion_id)}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </header>

        {loading && (
          <div className={styles.spinner}>
            <SpinnerIcon small />
          </div>
        )}

        {!loading && discussion_id && (
          <Chat
            message={message}
            user={user}
            onSendMessage={this.onSubmit}
            pollMessage={this.pollMessage}
          />
        )}
      </section>
    );
  }
}

export default withRouter(
  connect(
    ({ auth, user, messages, message }) => ({ auth, user, messages, message }),
    {
      getMessage: getMessageAction,
      sendMessage: sendMessageAction,
    }
  )(Message)
);
